import classNames from "classnames";
import { useCallback, useState } from "react";
import { downloadAndZipFiles } from "react-migration/domains/sites/helpers/downloadAndZipFiles";
import { BatchStatus, RemoteFile } from "react-migration/domains/sites/typings";
import { useTranslation } from "react-migration/lib";

import {
  DropdownMenuContentDimensions,
  DropdownMenuSize,
  DropdownMenuTriggerAlignment,
  Icon,
  DropdownMenu as LiDropdownMenu,
} from "@landtechnologies/components";

import { LetterEventName, logLetterEvent } from "../../helpers/logLetterEvent";

export type DropdownMenuProps = {
  batchId: string;
  name?: string;
  status: BatchStatus;
  letterPdfs: RemoteFile[];
};

export const DropdownMenu = ({ batchId, name, status, letterPdfs }: DropdownMenuProps) => {
  const { t } = useTranslation();

  const [{ height: menuHeight, width: menuWidth }, setMenuDimensions] =
    useState<DropdownMenuContentDimensions>({
      height: 0,
      width: 0,
    });
  const onMenuStateChanged = useCallback((dimensions: DropdownMenuContentDimensions) => {
    setMenuDimensions(dimensions);
  }, []);

  const onDownloadClick = useCallback(async () => {
    logLetterEvent({
      name: LetterEventName.LETTER_BATCHES_TABLE_DOWNLOAD_MENU_BUTTON_CLICKED,
      context: {
        pdfCount: letterPdfs.length,
      },
    });
    try {
      await downloadAndZipFiles({ fileReferences: letterPdfs, zipFolderName: name ?? "letters" });
      logLetterEvent({
        name: LetterEventName.LETTER_BATCHES_TABLE_DOWNLOAD_SUCCESSFUL,
        context: {
          pdfCount: letterPdfs.length,
        },
      });
    } catch (error) {
      logLetterEvent({
        name: LetterEventName.LETTER_BATCHES_TABLE_DOWNLOAD_FAILED,
        context: {
          pdfCount: letterPdfs.length,
        },
      });
      console.error({
        message: "Error fetching PDFs from signed URL",
        batchId: batchId,
        pdfUrls: letterPdfs.map(({ url }) => url),
      });
    }
  }, [batchId, letterPdfs, name]);
  const onRetryClick = useCallback(() => {
    console.log("RETRY ACTION TRIGGERED FOR BATCH ", batchId);
  }, [batchId]);
  const onCancelClick = useCallback(() => {
    console.log("CANCEL ACTION TRIGGERED FOR BATCH ", batchId);
  }, [batchId]);
  return (
    <div data-testid="letter-batches-dropdown-menu">
      <LiDropdownMenu.Provider
        size={DropdownMenuSize.AUTO}
        menuHeight={menuHeight}
        menuWidth={menuWidth}
      >
        <LiDropdownMenu.Trigger
          align={DropdownMenuTriggerAlignment.LEFT}
          data-testid="letter-batches-dropdown-trigger"
          tabIndex={-1}
          type="button"
        >
          <span
            className={classNames(
              "atlas-mx-1",
              "atlas-h-8",
              "atlas-w-8",
              "atlas-p-1",
              "atlas-rounded",
              "atlas-bg-transparent",
              "hover:atlas-bg-background-darker",
              "atlas-text-content-secondary",
              "atlas-text-center",
              "atlas-cursor-pointer"
            )}
          >
            <Icon icon="icon-lt-more-fill" size="sm" cursor="pointer" />
          </span>
        </LiDropdownMenu.Trigger>
        <LiDropdownMenu.Content onOpenStateChanged={onMenuStateChanged}>
          <LiDropdownMenu.Item
            onClick={onDownloadClick}
            data-testid="letter-batches-dropdown-option-download"
            disabled={status === BatchStatus.Queued}
          >
            {t("sites.letter_batches.download")}
          </LiDropdownMenu.Item>
          <LiDropdownMenu.Item
            onClick={onRetryClick}
            data-testid="letter-batches-dropdown-option-retry"
          >
            {t("sites.letter_batches.retry")}
          </LiDropdownMenu.Item>
          <LiDropdownMenu.Separator />
          <LiDropdownMenu.Item
            className={classNames("atlas-text-content-negative")}
            onClick={onCancelClick}
            data-testid="letter-batches-dropdown-option-cancel"
          >
            {t("sites.letter_batches.cancel")}
          </LiDropdownMenu.Item>
        </LiDropdownMenu.Content>
      </LiDropdownMenu.Provider>
    </div>
  );
};
